import React from 'react';
import LegalPage from './LegalPage';

export default function PrivacyNoticePage() {
  return (
    <LegalPage title="Privacy Notice" lastUpdated="April 30, 2026">
      <h2>1. Introduction</h2>
      <p>
        Tooling Management Group LLC (&ldquo;TMG,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) operates the ToolSight&trade; platform (the &ldquo;Service&rdquo;), a cloud-based tooling lifecycle management application for the automotive industry. This Privacy Notice explains how we collect, use, protect, and share information when you access or use the Service.
      </p>
      <p>
        This Privacy Notice applies to all users of the ToolSight platform, including TMG staff operating the platform on behalf of clients, supplier users accessing the supplier portal, and any other authorized users. It does not govern TMG&rsquo;s broader consulting engagement with clients, which is covered by the applicable Master Service Agreement and TMG&rsquo;s internal Data Privacy &amp; Confidentiality Policy.
      </p>
      <p>
        By signing in to the Service, you acknowledge that you have read and understood this Privacy Notice. For information about our full terms, please see the Terms of Use available at <a href="/legal/terms">/legal/terms</a>.
      </p>

      <h2>2. Information We Collect</h2>

      <h3>2.1 Account and Identity Information</h3>
      <p>When you are provisioned an account on the Service, we collect and store:</p>
      <ul>
        <li>Your name, email address, and other contact information, as provided by your organization or by TMG during onboarding</li>
        <li>Your organizational role and site assignments within the platform</li>
        <li>Authentication credentials managed through Microsoft Entra External ID (our identity provider)</li>
      </ul>
      <p>
        <strong>We do not collect Social Security numbers, government-issued identification numbers, financial account information, or other sensitive personal identifiers through the Service.</strong>
      </p>

      <h3>2.2 Tooling and Operational Data</h3>
      <p>The primary data processed by the Service relates to automotive tooling assets and is provided by or on behalf of our OEM clients. This includes:</p>
      <ul>
        <li>Tool specifications, asset tag numbers, and identification data</li>
        <li>Part numbers, manufacturing specifications and related information, and production status</li>
        <li>Supplier and manufacturing facility information</li>
        <li>Tool lifecycle data including condition assessments, warranty status, and end-of-warranty projections</li>
        <li>Disposal workflow records, including bills of lading and scrap job documentation</li>
        <li>Photographs and media files uploaded during field inventory or supplier operations</li>
        <li>Physical location data associated with tooling assets and supplier facilities</li>
      </ul>
      <p>
        This operational data is the property of the applicable OEM client. TMG processes it under the authority of the client&rsquo;s Master Service Agreement.
      </p>

      <h3>2.3 Usage and System Data</h3>
      <p>When you use the Service, we automatically collect:</p>
      <ul>
        <li>Authentication events (sign-in timestamps, session identifiers)</li>
        <li>Audit trail records of data creation, modification, and deletion actions</li>
        <li>Device type and browser information transmitted in standard HTTP headers</li>
      </ul>
      <p>
        <strong>We do not use third-party analytics services, advertising trackers, or social media pixels. The Service does not serve advertisements.</strong>
      </p>

      <h3>2.4 Cookies and Local Storage</h3>
      <p>The Service uses only essential cookies and browser local storage required for authentication and application functionality:</p>
      <ul>
        <li>Authentication session tokens issued by Microsoft Entra External ID</li>
        <li>Application state data stored in browser IndexedDB to support offline functionality</li>
      </ul>
      <p>
        <strong>We do not use marketing cookies, preference cookies, or any third-party tracking cookies.</strong> Because only essential cookies are used, no cookie consent banner is required.
      </p>

      <h2>3. How We Use Information</h2>
      <p>We use the information described above for the following purposes:</p>
      <ul>
        <li>To provide and operate the Service, including tool lifecycle tracking, disposal workflows, and supplier coordination</li>
        <li>To authenticate users and enforce role-based access controls</li>
        <li>To maintain audit trails for compliance, quality assurance, and client reporting</li>
        <li>To generate reports, analyses, and recommendations for our OEM clients</li>
        <li>To improve the reliability, security, and performance of the Service</li>
        <li>To communicate with users about the Service, including operational notifications</li>
      </ul>
      <p>We do not use your information for:</p>
      <ul>
        <li>Marketing or advertising to third parties</li>
        <li>Sale or rental to any third party</li>
        <li>Profiling or automated decision-making that produces legal or similarly significant effects on individuals</li>
        <li>Any purpose unrelated to the delivery of the Service under the applicable client agreement</li>
      </ul>

      <h2>4. Artificial Intelligence and Automated Processing</h2>
      <p>
        TMG uses artificial intelligence and automation tools to accelerate data processing and analysis within the Service. We believe transparency about AI usage is important, and we provide the following commitments:
      </p>

      <h3>4.1 How We Use AI</h3>
      <p>
        AI-assisted processing is used to support data mapping and enrichment, pattern recognition (such as identifying tool obsolescence indicators), cross-referencing multiple data sources, data quality validation, and report generation. AI is one tool in our expert toolkit &mdash; it augments, but does not replace, human judgment.
      </p>

      <h3>4.2 Human Oversight Guarantee</h3>
      <p>
        <strong>All AI-generated outputs are reviewed by experienced TMG subject matter experts before being finalized or delivered to clients.</strong> AI is never used to make autonomous final decisions on tool disposition, scrap authorization, or any action that could affect tooling status.
      </p>

      <h3>4.3 No Training on Your Data</h3>
      <p>
        <strong>Client data processed through the Service is not used to train, fine-tune, or improve any external AI model.</strong> Our AI platform operates under an enterprise agreement that explicitly prohibits the use of client data for model training. Your data processes your project only &mdash; it does not improve AI for other users, and it is not retained by the AI provider for training purposes.
      </p>

      <h3>4.4 Data Minimization in AI Processing</h3>
      <p>
        When data is processed through AI systems, TMG applies the principle of data minimization: only the data elements necessary for the specific analytical task are transmitted. Sensitive personal information such as passwords, credentials, and financial account numbers is never sent to AI systems. Data segmentation is maintained at all times &mdash; one client&rsquo;s data is never commingled with another&rsquo;s during AI processing.
      </p>

      <h2>5. How We Protect Information</h2>
      <p>
        TMG implements enterprise-grade security controls to protect information processed through the Service. Key measures include:
      </p>
      <ul>
        <li><strong>Encryption in transit:</strong> All data transmitted between your browser and the Service is encrypted using TLS 1.2 or higher.</li>
        <li><strong>Encryption at rest:</strong> All data stored in our cloud infrastructure (Azure Cosmos DB, Azure Blob Storage) is encrypted at rest using platform-managed keys.</li>
        <li><strong>Authentication and access control:</strong> The Service uses Microsoft Entra External ID for identity management with multi-factor authentication for privileged accounts. Role-based access controls ensure users can only access data relevant to their assigned sites and responsibilities.</li>
        <li><strong>Audit logging:</strong> All data modifications are recorded in an immutable audit trail, including the identity of the user, the timestamp, and the nature of the change.</li>
        <li><strong>Data isolation:</strong> Client data is logically separated. Users assigned to Site A cannot access data belonging to Site B. Supplier competitors&rsquo; data is maintained in complete separation.</li>
      </ul>
      <p>
        For complete details on our security practices, including incident response procedures, please refer to TMG&rsquo;s Information Security Policy, available upon request from your TMG engagement contact.
      </p>

      <h2>6. Who We Share Information With</h2>
      <p>
        TMG does not sell, rent, or share your personal information with third parties for their own marketing purposes. We share information only in the following circumstances:
      </p>

      <h3>6.1 Service Infrastructure Providers</h3>
      <p>The Service is hosted on Microsoft Azure, and we use the following infrastructure providers to operate the platform:</p>
      <ul>
        <li><strong>Microsoft Azure</strong> (Cosmos DB, Azure Functions, Static Web Apps, Blob Storage, Entra External ID) &mdash; primary cloud infrastructure, hosted in the United States. SOC 2 compliant, ISO 27001 certified.</li>
        <li><strong>Anthropic</strong> (Claude AI platform) &mdash; AI-assisted data processing and analysis, under enterprise agreement with explicit prohibition on model training using client data.</li>
      </ul>
      <p>
        These providers process data on our behalf under data processing agreements that require them to protect your information and restrict their use of it.
      </p>

      <h3>6.2 OEM Client Reporting</h3>
      <p>
        Tooling and operational data processed through the Service is reported to the applicable OEM client under the terms of TMG&rsquo;s engagement with that client. This is the core purpose of the Service.
      </p>

      <h3>6.3 Legal Requirements</h3>
      <p>
        We may disclose information if required by law, regulation, legal process, or governmental request, or if we believe disclosure is necessary to protect the rights, property, or safety of TMG, our clients, or others.
      </p>

      <h2>7. Data Retention</h2>
      <p>We retain information in accordance with the following schedule:</p>
      <ul>
        <li><strong>Active project data:</strong> Maintained for the duration of the client engagement plus 12 months.</li>
        <li><strong>Archived data:</strong> Retained for up to 3 years for reference, audit, and legal compliance purposes.</li>
        <li><strong>Audit trail records:</strong> Retained for the same period as the underlying project data.</li>
        <li><strong>Backup copies:</strong> Retained per disaster recovery schedule (up to 30 days), then permanently deleted.</li>
      </ul>
      <p>
        Upon client request or contract termination, client data is securely deleted within 30 calendar days. A certification of destruction is available upon request.
      </p>

      <h2>8. Data Location and Cross-Border Transfers</h2>
      <p>
        All data processed through the Service is stored and processed within the United States (Azure East US and North Central US regions). TMG does not transfer client data outside the United States without explicit written authorization from the applicable OEM client.
      </p>

      <h2>9. Your Rights</h2>
      <p>
        Depending on your jurisdiction, you may have certain rights regarding your personal information. These may include the right to:
      </p>
      <ul>
        <li>Access the personal information we hold about you</li>
        <li>Request correction of inaccurate personal information</li>
        <li>Request deletion of your personal information, subject to our retention obligations and applicable law</li>
        <li>Request a copy of your personal information in a portable, machine-readable format</li>
        <li>Object to or restrict certain processing activities</li>
      </ul>
      <p>
        To exercise any of these rights, please contact us using the information in Section 13 below. We will respond to verified requests within 30 days.
      </p>
      <p>
        <em>Note:</em> Because the majority of data in the Service is OEM client property (tooling and operational data rather than personal data), requests regarding that data should be directed to your employer or the applicable OEM client, who controls the data under the Master Service Agreement.
      </p>

      <h2>10. Children&rsquo;s Privacy</h2>
      <p>
        The Service is a business-to-business platform for the automotive industry and is not directed at individuals under 16 years of age. We do not knowingly collect personal information from children. If we learn that we have collected personal information from a child under 16, we will take steps to delete that information promptly.
      </p>

      <h2>11. Security Incident Notification</h2>
      <p>In the event of a confirmed security incident affecting data processed through the Service, TMG will:</p>
      <ul>
        <li>Notify affected clients within 24&ndash;72 hours of confirmation</li>
        <li>Provide a description of the incident, the data potentially affected, and the steps TMG is taking to remediate</li>
        <li>Designate a point of contact for questions and follow-up</li>
        <li>Comply with all applicable breach notification laws</li>
      </ul>
      <p>
        To report a suspected security concern, please contact us using the information in Section 13 below.
      </p>

      <h2>12. Changes to This Privacy Notice</h2>
      <p>
        We may update this Privacy Notice from time to time to reflect changes in our practices, legal requirements, or the Service itself.
      </p>
      <ul>
        <li><strong>Material changes:</strong> We will notify affected users by email at least 14 days before the changes take effect. The updated Privacy Notice will display a new &ldquo;Last Updated&rdquo; date.</li>
        <li><strong>Minor clarifications:</strong> Posted without individual notification. The &ldquo;Last Updated&rdquo; date will be revised.</li>
      </ul>
      <p>
        We encourage you to review this Privacy Notice periodically. Your continued use of the Service after changes become effective constitutes acceptance of the revised Privacy Notice.
      </p>

      <h2>13. Contact Us</h2>
      <p>
        If you have questions, concerns, or requests regarding this Privacy Notice or our data practices, please contact:
      </p>
      <p>
        <strong>Contact</strong><br />
        Tooling Management Group LLC<br />
        Attn: Chris Ongena, CEO<br />
        745 State Circle<br />
        Ann Arbor, MI 48108, USA<br />
        Email: <a href="mailto:chris@toolingmanagementgroup.com">chris@toolingmanagementgroup.com</a>
      </p>
      <p>
        <em>If you are an active user of the Service, your TMG account representative is also available for day-to-day questions.</em>
      </p>
    </LegalPage>
  );
}
